import { useState, useEffect, useCallback, useMemo } from 'react';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import Modal, { ModalProps } from '../../../../components/template/mvc-modal';
import Regex from '../../../../utils/regex';
import Toggle from '../../../../components/molecules/toggle';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../../utils/types/editable';
import Input from '../../../../components/atoms/input';
import UserService, {
	IUser,
	UserPermissions,
} from '../../../../services/User.service';
import ClientService, {
	ClientPermissions,
	IClient,
} from '../../../../services/Client.service';
import YardService, { IYard } from '../../../../services/Yard.service';
import { useRouteLoaderData } from 'react-router-dom';
import Select from '../../../../components/atoms/select';
import { HomeLoaderData } from '../../../../types/homeLoaderData';
import CarrierService, { ICarrier } from '../../../../services/Carrier.service';

export interface UserModalProps extends ModalProps<IUser> {
	setModalProps: React.Dispatch<React.SetStateAction<ModalProps<IUser>>>;
	permissionsName: { [key in keyof UserPermissions]: string };
}

const UsersModal = ({
	data,
	setModalProps,
	onClose,
	mode,
	permissionsName,
	isModalVisible,
	...props
}: UserModalProps) => {
	const { t } = useTranslation();

	const { user } = useRouteLoaderData('home') as HomeLoaderData;

	const [contas, definirContas] = useState<IClient[]>([]);
	const [yards, setYards] = useState<IYard[]>([]);
	const [carriers, setCarriers] = useState<ICarrier[]>([]);
	const [error, setError] = useState<string>('');
	const contaSelecionada = contas.find((conta) => conta.id === data.client?.id)
		?? data.client;

	const carregarContas = useCallback(async () => {
		const resposta = await ClientService.getAll();
		if (!resposta) {
			setError('alert.error.generic');
			return;
		}
		definirContas(resposta.data);
	}, []);

	useEffect(() => {
		if (isModalVisible) carregarContas();
	}, [isModalVisible, carregarContas]);

	// const reloadPermissions = useCallback(async () => {
	// 	setModalProps((prev) => ({
	// 		...prev,
	// 		data: {
	// 			...prev.data,
	// 			permissions: {
	// 				...prev.data.permissions,
	// 				isMaintenance: false,
	// 				isMonitoring: false,
	// 				isMovement: false,
	// 				isReport: false,
	// 			},
	// 		},
	// 	}));
	// }, [data.client]);

	// useEffect(() => {
	// 	reloadPermissions();
	// }, [reloadPermissions]);

	const loadYards = useCallback(async () => {
		setYards([]);
		if (!data.client?.id) return;
		const response = await YardService.getAll({
			idClient: data.client.id,
			isActive: 1,
		});
		return response;
	}, [data.client?.id]);

	useEffect(() => {
		let carregamentoAtual = true;
		loadYards().then((response) => {
			if (carregamentoAtual && response) setYards(response.data);
		});
		return () => { carregamentoAtual = false; };
	}, [loadYards]);

	
	const loadCarriers = useCallback(async () => {
		setCarriers([]);
		if (!data.client?.id) return;
		const response = await CarrierService.getAll({
			idClient: data.client.id,
			isActive: 1,
		});
		return response;
	}, [data.client?.id]);

	useEffect(() => {
		let carregamentoAtual = true;
		loadCarriers().then((response) => {
			if (carregamentoAtual && response) setCarriers(response.data);
		});
		return () => { carregamentoAtual = false; };
	}, [loadCarriers]);

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const onSubmit = () => {
		if (!validate()) return;
		if (mode === 'edit') {
			onEdit();
			return;
		}
		onCreate();
	};

	const validate = (): boolean => {
		if (
			!data.name || !data.email || !data.phone ||
			!Number.isInteger(data.client?.id) || !data.client?.id ||
			data.client.id < 1
		) {
			setError('alert.error.mandatory');
			return false;
		}
		if (!Regex.Email(data.email)) {
			setError('alert.error.email');
			return false;
		}
		if (!Regex.Phone(data.phone)) {
			setError('alert.error.phone');
			return false;
		}
		return true;
	};

	const onCreate = async () => {
		const response = await UserService.create(data, setError);
		if (!response) return;
		NotificationToast.Success(
			`${t('admin.user')} ${t('alert.success.create')}`
		);
		onClose(true);
	};

	const onEdit = async () => {
		const response = await UserService.edit(data, setError);
		if (!response) return;
		NotificationToast.Success(
			`${t('admin.user')} ${t('alert.success.update')}`
		);
		onClose(true);
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				[e.target.name]: e.target.value,
			},
		}));
	};

	const selecionarConta = (evento: EditableChangeEvent) => {
		const conta = contas.find(
			(item) => `${item.fantasyName} - ${item.cnpj}` === evento.target.value
		);
		if (!conta || conta.id === data.client?.id) return;
		setYards([]);
		setCarriers([]);
		setModalProps((prev) => ({
			...prev,
			data: { ...prev.data, client: conta, yards: [], carriers: [] },
		}));
	};

	const handlePermissions = (e: EditableChangeEvent) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				permissions: {
					...prev.data.permissions,
					[e.target.name]: e.target.value,
				},
			},
		}));
	};

	const handleYards = (e: EditableChangeEvent) => {
		const yardsUpdated: Partial<IYard>[] = (e.target.value as string[]).map(
			(item) => {
				const idYard = yards.find((yard) => yard.name === item)?.id;
				return {
					id: idYard,
					name: item,
				};
			}
		);
		handleInput({ target: { name: 'yards', value: yardsUpdated } });
	};

	const handleCarriers = (e: EditableChangeEvent) => {
		const carriersUpdated: Partial<ICarrier>[] = (e.target.value as string[]).map(
			(item) => {
				const idCarrier = carriers.find((carrier) => carrier.name === item)?.id;
				return {
					id: idCarrier,
					name: item,
				};
			}
		);
		handleInput({ target: { name: 'carriers', value: carriersUpdated } });
	};

	const renderToggles = () => (
		<div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
			<Toggle
				name="isMonitoring"
				value={data.permissions.isMonitoring}
				label={permissionsName.isMonitoring}
				onChange={handlePermissions}
				disabled={
					mode === 'view' ||
					(!user.permissions.isSystemAdmin && !user.permissions.isLocalAdmin)
				}
			/>
			<Toggle
				name="isMovement"
				value={data.permissions.isMovement}
				label={permissionsName.isMovement}
				onChange={handlePermissions}
				disabled={
					mode === 'view' ||
					(!user.permissions.isSystemAdmin && !user.permissions.isLocalAdmin)
				}
			/>

			<Toggle
				name="isMaintenance"
				value={data.permissions.isMaintenance}
				label={permissionsName.isMaintenance}
				onChange={handlePermissions}
				disabled={
					mode === 'view' ||
					(!user.permissions.isSystemAdmin && !user.permissions.isLocalAdmin)
				}
			/>

			<Toggle
				name="isReport"
				value={data.permissions.isReport}
				label={permissionsName.isReport}
				onChange={handlePermissions}
				disabled={
					mode === 'view' ||
					(!user.permissions.isSystemAdmin && !user.permissions.isLocalAdmin)
				}
			/>

			{user.permissions.isSystemAdmin && (
				<Toggle
					name="isSystemAdmin"
					value={data.permissions.isSystemAdmin}
					label={permissionsName.isSystemAdmin}
					onChange={handlePermissions}
					disabled={mode === 'view'}
				/>
			)}

			<Toggle
				name="isLocalAdmin"
				value={data.permissions.isLocalAdmin}
				label={permissionsName.isLocalAdmin}
				onChange={handlePermissions}
				disabled={mode === 'view'}
			/>
			<Toggle
				name="shouldReceiveAlerts"
				value={data.shouldReceiveAlerts}
				label={t('misc.alerts')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
			<Toggle
				name="acessoFluxoUnico"
				value={data.acessoFluxoUnico}
				label={t('fluxo_unico.login.container_tracking')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
			<Toggle
				name="isActive"
				value={data.isActive}
				label={t('status.active')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
		</div>
	);

	const renderInputs = () => (
		<div className="grid grid-cols-2 gap-x-2 gap-y-3">
			<Select
				className="col-span-full"
				name="client"
				value={contaSelecionada?.id
					? `${contaSelecionada.fantasyName} - ${contaSelecionada.cnpj}`
					: ''}
				label={t('admin.client')}
				placeholder={t('admin.client')}
				options={contas.map((conta) => `${conta.fantasyName} - ${conta.cnpj}`)}
				onChange={selecionarConta}
				disabled={mode === 'view'}
				required
			/>
			<Input
				containerClassName="col-span-full"
				value={data.name}
				name="name"
				label={t('misc.name')}
				placeholder={t('misc.name')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
			<Input
				containerClassName="col-span-full"
				value={data.email}
				name="email"
				label={t('misc.email')}
				placeholder={t('misc.email')}
				onChange={handleInput}
				disabled={mode === 'view'}
				type="email"
				required
			/>
			<Input
				value={data.cpf}
				name="cpf"
				label={t('admin.users.cpf')}
				placeholder={t('admin.users.cpf')}
				onChange={handleInput}
				disabled={mode === 'view'}
				mask="999.999.999-99"
			/>
			<Input
				value={data.phone}
				name="phone"
				label={t('misc.phone')}
				placeholder={t('misc.phone')}
				onChange={handleInput}
				disabled={mode === 'view'}
				mask="+9999999999999"
				type="phone"
				required
			/>
			<Input
				containerClassName="col-span-full"
				value={data?.role || ''}
				name="role"
				label={t('admin.users.role')}
				placeholder={t('admin.users.role')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
		</div>
	);

	return (
		<Modal<IUser>
			className=""
			onClose={onClose}
			data={data}
			mode={mode}
			onConfirm={onSubmit}
			isModalVisible={isModalVisible}
			{...props}
		>
			{renderToggles()}
			{renderInputs()}
			<Select
				name="yards"
				value={data.yards?.map((item) => item.name || '') || []}
				label={t('port_management.yards.title')}
				placeholder={t('port_management.yards.title')}
				options={yards.map((item) => item.name)}
				onChange={handleYards}
				isMulti
			/>
			<Select
				name="carriers"
				value={data.carriers?.map((item) => item.name || '') || []}
				label={t('port_management.carriers.title')}
				placeholder={t('port_management.carriers.title')}
				options={carriers.map((item) => item.name)}
				onChange={handleCarriers}
				isMulti
			/>
		</Modal>
	);
};

export default UsersModal;
